/* COPY of fleet-testing/shared/errors/reporter.js — do not edit here; edit the master and run
 * `node shared/errors/sync.js` from fleet-testing. */
/* error-reporter v1 — fleet-testing/shared/errors/reporter.js
 *
 * Shared across projects. Apps keep a COPY of this file (a deployed app cannot import from
 * another repo); `node shared/errors/sync.js` refreshes every copy listed in the projects'
 * errors.json. Edit the master in fleet-testing, never a copy.
 *
 * Catches, in a real user's browser:
 *   - uncaught errors and unhandled promise rejections (React error boundaries that call the
 *     native window.reportError land here too)
 *   - failed HTTP calls to the backends you list (status >= 400), with the server's error code
 *   - anything passed to report(error, extra)
 * and POSTs a small JSON record to `endpoint`. No framework or backend dependency: any endpoint
 * that accepts a JSON POST works (a Supabase table via PostgREST does).
 *
 * Privacy: emails, phone-like digit runs, bearer/JWT tokens, query-string VALUES and anything
 * matched by the app's `redact` list (e.g. access tokens in URL paths) are removed
 * before anything leaves the browser. No request or response bodies are sent, only the server's
 * error code and (scrubbed) message. Context is whatever your getContext() returns, so keep
 * names and contact details out of it.
 */

const VERSION = 'error-reporter/1';
const MAX_PER_PAGE = 25;          // records sent per page load, total
const MAX_PER_FINGERPRINT = 3;    // the same error repeated in a loop is sent this many times
const MAX_TEXT = 2000;

const DEFAULT_IGNORE = [
  /ResizeObserver loop/i,
  /^Script error\.?$/i,           // cross-origin script with no details
  /chrome-extension:|moz-extension:|safari-extension:/i,
  /Load failed$|Failed to fetch$|NetworkError when attempting/i, // offline / flaky network
  /AbortError|The user aborted a request/i,
];

// Per-app [RegExp, replacement] pairs for secrets that look like ordinary data, e.g. access
// tokens carried in a URL path. Applied to every text field and URL before anything is sent.
let redactions = [];

export function scrub(text) {
  if (text == null) return text;
  let out = String(text);
  for (const [re, rep] of redactions) out = out.replace(re, rep);
  return out
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
    .replace(/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, '[jwt]')
    .replace(/\b(Bearer|apikey)\s+[\w.-]+/gi, '$1 [token]')
    // No lookbehind: older Safari fails to PARSE it, which would take the whole bundle down.
    .replace(/(^|[^\w-])(\+?\d[\d\s().-]{8,}\d)(?![\w-])/g, (m, pre, num) => (
      // Phone-like: 10+ digits. Dates and timestamps (2026-10-18 05:33) are kept — they are
      // often the whole point of a database error message.
      num.replace(/\D/g, '').length >= 10 && !/\d{4}-\d{2}-\d{2}/.test(num) ? `${pre}[number]` : m))
    .slice(0, MAX_TEXT);
}

// Path kept, query VALUES dropped: ?id=eq.123&select=* -> ?id=&select=
export function scrubUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    const keys = [...u.searchParams.keys()];
    return scrub(u.origin + u.pathname) + (keys.length ? '?' + keys.map((k) => `${k}=`).join('&') : '');
  } catch (e) {
    return scrub(String(url).split('?')[0]);
  }
}

// The deployed bundle's hash (main.<hash>.js) identifies the release without a build step.
function detectRelease() {
  const s = [...document.scripts].map((x) => x.src).find((src) => /\/main\.[a-f0-9]+\.js/.test(src));
  return s ? s.match(/main\.([a-f0-9]+)\.js/)[1] : 'unknown';
}

let state = null;

export function initErrorReporter(config) {
  if (state || typeof window === 'undefined') return;
  const cfg = {
    project: config.project,
    app: config.app || config.project,
    endpoint: config.endpoint,
    headers: config.headers || {},
    release: config.release || detectRelease(),
    getContext: config.getContext || (() => ({})),
    watchUrls: config.watchUrls || [],             // substrings of backend URLs to watch for failures
    ignore: [...DEFAULT_IGNORE, ...(config.ignore || [])],
    ignoreHttp: config.ignoreHttp || (() => false), // (status, code, url) => true to skip
    enabled: config.enabled !== false,
  };
  redactions = (config.redact || []).map(([re, rep]) => [new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g'), rep]);
  if (!cfg.enabled || !cfg.endpoint) return;
  const originalFetch = window.fetch.bind(window);
  state = { cfg, sent: 0, seen: new Map(), originalFetch };

  window.addEventListener('error', (ev) => {
    // Resource load failures (img/script) have no .error; only report script errors.
    if (!ev.error && !ev.message) return;
    send('error', ev.error || new Error(ev.message), { source: ev.filename, line: ev.lineno, col: ev.colno });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const r = ev.reason;
    send('rejection', r instanceof Error ? r : new Error(typeof r === 'string' ? r : safeJson(r)));
  });

  if (cfg.watchUrls.length) {
    window.fetch = async (input, init) => {
      const res = await originalFetch(input, init);
      try {
        const url = typeof input === 'string' ? input : input?.url || '';
        if (res.status >= 400 && url !== cfg.endpoint && !url.startsWith(cfg.endpoint)
            && cfg.watchUrls.some((w) => url.includes(w))) {
          const method = (init?.method || input?.method || 'GET').toUpperCase();
          res.clone().json().catch(() => ({})).then((body) => {
            const code = body?.code || body?.error_code || body?.error || null;
            if (cfg.ignoreHttp(res.status, code, url, method)) return;
            const msg = `${method} ${res.status}${code ? ' ' + code : ''}: ${body?.message || body?.msg || res.statusText}`;
            send('http', new Error(msg), {
              status: res.status, code, method, url: scrubUrl(url),
              details: body?.details || body?.hint || null,
            });
          });
        }
      } catch (e) { /* never let reporting break the app's request */ }
      return res;
    };
  }
}

export function report(error, extra) {
  send('manual', error instanceof Error ? error : new Error(String(error)), extra);
}

function safeJson(v) {
  try { return JSON.stringify(v); } catch (e) { return String(v); }
}

function topFrame(stack) {
  const line = String(stack || '').split('\n').find((l) => /:\d+:\d+/.test(l)) || '';
  return line.replace(/\?[^:)]*/, '').trim();
}

function send(kind, error, extra = {}) {
  if (!state) return;
  const { cfg } = state;
  const message = scrub(error?.message || String(error));
  if (cfg.ignore.some((re) => re.test(message) || re.test(String(error?.stack || '')))) return;
  if (state.sent >= MAX_PER_PAGE) return;

  // Same error at the same place = same fingerprint; the reader groups on it across browsers.
  const frame = topFrame(error?.stack);
  const fingerprint = hash(`${kind}|${extra.status || ''}|${extra.code || ''}|${
    kind === 'http' ? (extra.url || '').split('?')[0] : message}|${frame.replace(/\.[a-f0-9]{8}\./, '.')}`);
  const n = (state.seen.get(fingerprint) || 0) + 1;
  state.seen.set(fingerprint, n);
  if (n > MAX_PER_FINGERPRINT) return;
  state.sent += 1;

  let ctx = {};
  try { ctx = cfg.getContext() || {}; } catch (e) { /* context is optional */ }
  const record = {
    project: cfg.project,
    app: cfg.app,
    release: cfg.release,
    kind,
    fingerprint,
    message,
    stack: scrub(error?.stack || '').slice(0, 8000),
    page: scrubUrl(window.location.href),
    user_agent: navigator.userAgent.slice(0, 300),
    context: { ...ctx, ...Object.fromEntries(Object.entries(extra).map(([k, v]) => [k, typeof v === 'string' ? scrub(v) : v])) },
    reporter: VERSION,
  };
  state.originalFetch(cfg.endpoint, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Prefer: 'return=minimal', ...cfg.headers },
    body: JSON.stringify(record),
  }).catch(() => { /* reporting must never throw */ });
}

function hash(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0).toString(16).padStart(8, '0');
}
